import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  DoctorSetupDetail,
  StaffSpeciality,
} from '@/types/settings-api';

/**
 * Wrapper the backend returns from `POST /api/Doctor`. Verified against
 * working dev cURL 2026-05-21 (PROD-872):
 *
 *   { "doctorId": { "id": "<doctorSetupId>", "status": true,
 *                    "mailSent": true, "userId": "<userId>" } }
 *
 * - `id` is the newly-assigned `doctorSetupId` — use this to drive
 *   `useDoctorSetupDetails(id)` for follow-on tabs.
 * - `userId` is the practitioner's auth user — used by per-tab edit
 *   endpoints that key on `userId` instead of `doctorSetupId` (Personal /
 *   Professional / Educational).
 * - `mailSent: true` confirms the invite email dispatched.
 */
export interface AddDoctorResponse {
  doctorId: {
    id: string;
    status: boolean;
    mailSent: boolean;
    userId: string;
  };
}

/**
 * Wire body for `POST /api/Doctor` — produced by `buildCreateDoctorBody`.
 *
 * This is intentionally the same shape as `DoctorSetupDetail` (PUT body)
 * because the backend reads both via the same model binder. **Do not
 * hand-roll this object at the call site.** Always go through the
 * mapper so the legacy-compat fields (empty-string / null sentinels per
 * the captured dev curl) are written in one place.
 */
export type CreateDoctorBody = DoctorSetupDetail;

/**
 * User-facing fields a caller actually collects from the form. The
 * mapper layers in the curl-verified legacy-compat fields.
 */
export interface CreateDoctorInput {
  organizationId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  /** Combined country-dial-then-digits wire form, e.g. `"+971-554654645"`. */
  mobileNumber: string;
  /** Numeric `TenantRole.roleId` — comes from `useRolesByType`. */
  role: number;
  doctorSpecialities: StaffSpeciality[];
}

/**
 * Compose the wire body for `POST /api/Doctor` from user-facing inputs.
 *
 * **Why this exists:** the create-body shape (verified against dev curl
 * 2026-05-21) includes six legacy-compat fields that MUST be present but
 * are not part of the user's mental model:
 *
 *   - `doctorSetupId: ""`          (backend assigns; empty string, NOT omitted)
 *   - `doctorSpecialityIds: []`    (legacy parallel array)
 *   - `speciality: ""`             (legacy singular GUID — empty STRING, not null)
 *   - `specialities: null`         (legacy singular full code — null)
 *   - `isLastLogin: null`
 *   - `photoURL: ""`               (uploaded via separate flow)
 *
 * Baking these into the mutation hook would lock the wire shape into
 * the transport layer (Samer review PR #323 — separation of concerns).
 * Baking them into each call site would diverge the moment a second
 * caller (Settings → Practitioners → Add) lands. This mapper is the
 * single source of truth.
 */
export function buildCreateDoctorBody(input: CreateDoctorInput): CreateDoctorBody {
  return {
    organizationId: input.organizationId,
    doctorSetupId: '',
    firstName: input.firstName,
    lastName: input.lastName,
    emailId: input.emailId,
    mobileNumber: input.mobileNumber,
    role: input.role,
    isActive: true,
    doctorSpecialities: input.doctorSpecialities,
    doctorSpecialityIds: [],
    speciality: '',
    isLastLogin: null,
    specialities: null,
    photoURL: '',
  };
}

/**
 * Create a new practitioner — POST /api/Doctor.
 *
 * Powers BOTH the wizard's PR 4 Add Practitioners flow AND the previously-
 * stubbed Settings → Practitioners create flow (`practitioner-edit-page.tsx:96-105`).
 *
 * **Cache invalidation is NOT done in this hook** (Samer review PR #323).
 * The caller controls when the practitioner list refetches — typically
 * AFTER any post-create UI (confirmation pane, follow-on sheet) has
 * unmounted, to avoid a flash of the underlying list re-rendering
 * while the sheet is still visible.
 *
 * Recommended caller pattern:
 *
 *   const create = useCreateDoctor();
 *   const queryClient = useQueryClient();
 *   // ... on Sheet close (NOT in mutation onSuccess):
 *   queryClient.invalidateQueries({ queryKey: ['settings', 'doctors-list'] });
 */
export function useCreateDoctor() {
  return useMutation({
    mutationFn: (body: CreateDoctorBody) =>
      post<AddDoctorResponse>('/api/Doctor', body),
  });
}
